import { useCallback, useMemo, useState } from 'react'
import { isEmpty } from 'ramda'

import { useCompensationStatements, useCompensationStatementsActions } from 'app/dataProviders/cafeteria/compensation/statements/hooks'
import SimpleDate from 'app/common/classes/simple-date'
import Sticky from 'app/common/templates-next/sticky'
import { SkeletonLoadingTBody, StatusMessage } from 'app/common/templates-next/page-template'

import {
  CheckStateProvider,
  fancyRowRootClassName,
  Table,
  useCopyColumnsWidths,
  useTopShiftState,
} from 'app/common/ui-components/table'

import { useColumnProps, useColumnTitles, useColumnsArranged, useHeaderColumnProps } from './columns'
import { TableHeader } from './TableHeader'
import { TableRow } from './TableRow'


type Props = {
  studentID: string
  supplierID: string
  periodFrom: string
  periodTo: string
  periodFormat: string
  currency: string
  onRedirectWithdrawToParent: (ID: string) => void
}

export const StatementsTable = ({
  studentID,
  supplierID,
  periodFrom,
  periodTo,
  periodFormat,
  currency,
  onRedirectWithdrawToParent,
}: Props) => {
  const params = useMemo(() => {
    const dateFrom = new SimpleDate(periodFrom, periodFormat)
    const dateTo = new SimpleDate(periodTo, periodFormat)
    return { studentID, supplierID, dateFrom, dateTo }
  }, [studentID, supplierID, periodFrom, periodTo, periodFormat])

  const { statements, loading, request } = useCompensationStatements({ params })
  const { pendingIDs, remove } = useCompensationStatementsActions({ onRemoveSuccess: request })
  const nothing = isEmpty(statements)

  const columns = useColumnsArranged()
  const titles = useColumnTitles(currency)
  const headerColumnProps = useHeaderColumnProps()
  const columnProps = useColumnProps()

  const [updateRowNode, calcTopShift, nodes] = useTopShiftState()

  const [trFrom, trTo] = useCopyColumnsWidths([], { useClassIndexes: true })

  return (
    <Table wide>
      <thead>
        <Sticky ref={trFrom} applyToChildrenTh>
          {(ref, stuck, shadow) => (
            <TableHeader
              ref={ref}
              columns={columns}
              titles={titles}
              stuck={stuck}
              shadow={shadow}
              columnProps={headerColumnProps}
            />
          )}
        </Sticky>
      </thead>
      <tbody className={fancyRowRootClassName}>
        {nothing
          && loading
          && <SkeletonLoadingTBody
            rows={statements.length || 5}
            columns={columns.length}
            onlyRows
          />}

        {nothing
          && !loading
          && <StatusMessage asTableRow colSpan={columns.length}>
            {'Нет записей, удовлетворяющих условиям'}
          </StatusMessage>}

        {!nothing
          && <CheckStateProvider>
            {statements.map((statement, index) => (
              <TableRow
                key={statement.ID}
                columns={columns}
                columnProps={columnProps}
                statement={statement}
                index={index}
                calcTopShift={calcTopShift}
                trElem={nodes[String(index)]}
                updateRowNode={updateRowNode}
                onRemove={remove}
                onEdit={onRedirectWithdrawToParent}
                disabled={loading || pendingIDs.has(statement.ID)}
                innerTableHeaderRef={trTo}
              />
            ))}
          </CheckStateProvider>}
      </tbody>
    </Table>
  )
}
